import { useRef, useState } from "react";
import { Camera, Upload, X, Loader2, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { API_BASE } from "../lib/api";

/**
 * Container number OCR scanner.
 *
 * Renders a small button. When clicked, opens a dialog where the user can
 *   - capture from device camera (mobile)
 *   - or upload an image file (desktop)
 *
 * The image is POSTed to /api/ocr/container-number, and onResult(number) is
 * called with the cleaned ISO 6346 number on success.
 *
 * Props:
 *  - onResult(number)
 *  - label?: button label
 *  - small?: render as a small icon-only button
 *  - dataTestid?: testid root
 */
export default function ContainerNumberScanner({ onResult, label = "Scan", small = false, dataTestid = "ocr" }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);  // { container_number, confidence }
  const [error, setError] = useState("");
  const cameraRef = useRef(null);
  const fileRef = useRef(null);

  const reset = () => {
    setPreview(null);
    setFile(null);
    setResult(null);
    setError("");
  };
  const onClose = () => { setOpen(false); reset(); };

  const handleFile = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/jpg"].includes(f.type)) {
      setError(`Unsupported file type ${f.type}. Use JPEG, PNG, or WEBP.`);
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setError("Image too large (max 8 MB)");
      return;
    }
    setError("");
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/ocr/container-number`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "OCR failed");
      setResult(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = () => {
    if (!result?.container_number) return;
    onResult(result.container_number);
    onClose();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={small
          ? "ps-btn ps-btn-secondary h-8 px-2"
          : "ps-btn ps-btn-secondary"
        }
        title="Scan container number from image"
        data-testid={`${dataTestid}-open`}
      >
        <Camera className="w-4 h-4" /> {!small && label}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-start justify-center p-4 overflow-y-auto" data-testid={`${dataTestid}-dialog`}>
          <div className="bg-white max-w-lg w-full mt-12 rounded-sm shadow-xl">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">OCR Scanner</div>
                <h2 className="font-display text-lg font-extrabold">Scan container number</h2>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-900" data-testid={`${dataTestid}-close`}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {error && (
                <div className="border border-red-200 bg-red-50 text-red-900 text-sm p-3 flex items-start gap-2" data-testid={`${dataTestid}-error`}>
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><div>{error}</div>
                </div>
              )}

              {/* Result */}
              {result && (
                <div className="border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid={`${dataTestid}-result`}>
                  <div className="flex items-center gap-2 text-emerald-900">
                    <CheckCircle2 className="w-4 h-4" />
                    <div className="text-xs font-bold uppercase tracking-[0.16em]">Detected</div>
                  </div>
                  <div className="font-mono font-black text-3xl text-gray-900">{result.container_number}</div>
                  <div className="text-xs text-gray-600">
                    Confidence: <span className="font-bold uppercase">{result.confidence}</span>
                  </div>
                </div>
              )}

              {/* Preview */}
              {preview && (
                <div className="border border-gray-200 bg-gray-50 p-2">
                  <img src={preview} alt="Captured" className="w-full max-h-64 object-contain" />
                </div>
              )}

              {/* Pickers */}
              {!result && (
                <div className="grid grid-cols-2 gap-2">
                  <input
                    ref={cameraRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid={`${dataTestid}-camera-input`}
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid={`${dataTestid}-file-input`}
                  />
                  <button
                    type="button"
                    onClick={() => cameraRef.current?.click()}
                    className="ps-btn ps-btn-primary"
                    data-testid={`${dataTestid}-use-camera`}
                  >
                    <Camera className="w-4 h-4" /> Use camera
                  </button>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="ps-btn ps-btn-secondary"
                    data-testid={`${dataTestid}-upload`}
                  >
                    <Upload className="w-4 h-4" /> Upload file
                  </button>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-1">
                {result ? (
                  <>
                    <button type="button" onClick={reset} className="ps-btn ps-btn-secondary" data-testid={`${dataTestid}-rescan`}>
                      <RefreshCw className="w-4 h-4" /> Scan again
                    </button>
                    <button type="button" onClick={confirm} className="ps-btn ps-btn-primary" data-testid={`${dataTestid}-confirm`}>
                      <CheckCircle2 className="w-4 h-4" /> Use {result.container_number}
                    </button>
                  </>
                ) : (
                  <>
                    {file && (
                      <button type="button" onClick={reset} className="ps-btn ps-btn-secondary">
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={!file || submitting}
                      onClick={submit}
                      className="ps-btn ps-btn-primary"
                      data-testid={`${dataTestid}-submit`}
                    >
                      {submitting
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</>
                        : <><Camera className="w-4 h-4" /> Read number</>}
                    </button>
                  </>
                )}
              </div>
              <p className="text-[11px] text-gray-500">
                Tip: capture the ISO 6346 marking (4 letters + 7 digits). Avoid the size/type code (22G1/45G1) and the carrier logo.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
